import { useMemo, useState } from "react";
import { GICSSubIndustries } from "@/lib/utils";
import { EditableField, type FieldStatus } from "./editable-field";
import type { FieldSource } from "./field-sources";
import { VirtualizedMultiSelectField } from "./virtualized-multi-select";
import { useEditDealField } from "./use-edit-deal-field";
import { useStageDraftInBulk } from "./bulk-edit-context";

// Shape-based validator. The downstream consumer requires `sub_industries`
// to contain GICS codes (8-digit parent or 10-digit sub-industry); legacy
// rows can carry human-readable names like "Retail" / "Fashion" inherited
// from older write paths, and re-saving such a row would otherwise persist
// those names back into the canonical fields.
const isGicsCode = (s: string): boolean => /^\d{8}$|^\d{10}$/.test(s);

/**
 * Industry picker. Uses the chips-above + virtualized-dropdown UI (same
 * external look as Business Model, but virtualized for the ~1,500 GICS
 * sub-industries). Persists `sub_industries: [codes]` and derives parent
 * 8-digit `industries` codes on save.
 */
export function EditableIndustryField({
  subIndustryCodes,
  status,
  sources,
}: {
  subIndustryCodes: string[];
  status?: FieldStatus;
  sources?: FieldSource[];
}) {
  const { save } = useEditDealField();
  const [draft, setDraft] = useState<string[]>(subIndustryCodes);
  // Drop legacy non-code values (e.g. "Retail") from any write so the
  // canonical sub_industries / industries fields always carry GICS codes.
  const validDraft = useMemo(() => draft.filter(isGicsCode), [draft]);
  useStageDraftInBulk("sub_industries", validDraft);
  // Parent industry codes are derived from the first 8 chars of each
  // sub-industry code. Stage them alongside so a bulk save persists both
  // fields together (matches the legacy per-field save which wrote both).
  const parentIndustryCodes = useMemo(
    () => [...new Set(validDraft.map((code) => code.substring(0, 8)))],
    [validDraft]
  );
  useStageDraftInBulk("industries", parentIndustryCodes);

  const options = useMemo(
    () =>
      GICSSubIndustries.map((si) => ({
        value: si.code,
        label: si.name,
        description: si.description,
      })),
    []
  );

  // Different GICS sub-industry codes can resolve to the same display name
  // (and the AI sometimes pushes a code twice), so dedupe on the resolved
  // name to avoid React's "two children with the same key" warning and the
  // visual duplicate badge that comes with it.
  const readNames = Array.from(
    new Set(
      subIndustryCodes
        .map((code) => GICSSubIndustries.find((si) => si.code === code)?.name)
        .filter((name): name is string => Boolean(name)),
    ),
  );

  return (
    <EditableField
      label="Industry"
      status={status}
      sources={sources}
      readValue={
        readNames.length > 0 ? (
          <div className="flex flex-wrap gap-1">
            {readNames.map((name) => (
              <span
                key={name}
                className="rounded-full bg-muted px-2 py-0.5 text-xs font-medium"
              >
                {name}
              </span>
            ))}
          </div>
        ) : null
      }
      editor={
        <VirtualizedMultiSelectField
          options={options}
          selected={draft}
          onChange={setDraft}
          placeholder="Select industry tags"
          searchPlaceholder="Search industry tags…"
          renderItemTooltip={(o) =>
            o.description ? (
              <div className="space-y-1">
                <p className="text-[10px] uppercase tracking-wider text-primary-foreground/60">
                  Sub-Industry
                </p>
                <p className="text-sm font-semibold">{o.label}</p>
                <p>{o.description}</p>
              </div>
            ) : null
          }
        />
      }
      onEnterEdit={() => setDraft(subIndustryCodes)}
      onApprove={
        subIndustryCodes.length > 0
          ? async () => {
              const validCodes = subIndustryCodes.filter(isGicsCode);
              const parentCodes = [
                ...new Set(validCodes.map((c) => c.substring(0, 8))),
              ];
              await save("sub_industries", validCodes);
              await save("industries", parentCodes);
            }
          : undefined
      }
    />
  );
}
